// src/services/EnhancedColumnIdentifier.ts

import { StandardTrade } from '../types/database';

/**
 * Risultato dell'analisi delle colonne
 */
export interface ColumnAnalysisResult {
  mapping: { [csvHeader: string]: keyof StandardTrade };
  confidence: number;
  detectedPlatform: string | null;
  unmappedColumns: string[];
}

/**
 * Pattern di riconoscimento per una colonna
 */
interface ColumnPattern {
  field: keyof StandardTrade;
  patterns: RegExp[];
  validators: ((value: string) => boolean)[];
  platformHints?: string[]; // Piattaforme che usano tipicamente questo nome
}

/**
 * Template per piattaforme specifiche
 */
interface PlatformTemplate {
  name: string;
  signatures: RegExp[]; // Pattern per identificare la piattaforma
  columnMappings: { [csvHeader: string]: keyof StandardTrade };
  confidence: number; // Confidence boost se la piattaforma è identificata
}

/**
 * Sistema avanzato di identificazione e mapping delle colonne CSV
 * Supporta 10+ piattaforme di trading con riconoscimento automatico
 */
export class EnhancedColumnIdentifier {
  /**
   * 🆕 Converte numeri in formato italiano a formato standard
   * Esempi:
   * - "76,304" → 76.304
   * - "1.000,50" → 1000.50
   * - "-366,52" → -366.52
   * - "€2.497,98" → 2497.98
   */
  private static parseItalianNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined) return null;

    // Se è già un numero, ritornalo
    if (typeof value === 'number') return value;

    // Converti a stringa e pulisci
    let str = String(value).trim();

    // Rimuovi spazi, simboli di valuta e altri caratteri non numerici (eccetto . , -)
    str = str
      .replace(/[€$£¥\s]/g, '') // Rimuovi simboli di valuta e spazi
      .replace(/\s/g, ''); // Rimuovi spazi residui

    // Se vuoto, ritorna null
    if (str === '' || str === '-') return null;

    // Formato italiano: 1.000,50 → 1000.50
    // Logica: se ha sia . che ,, il punto è separatore migliaia e la virgola è decimale
    if (str.includes('.') && str.includes(',')) {
      // Es: "1.000,50" → rimuovi punto (migliaia), sostituisci virgola con punto
      str = str.replace(/\./g, '').replace(',', '.');
    }
    // Solo virgola: è separatore decimale
    else if (str.includes(',')) {
      str = str.replace(',', '.');
    }
    // Solo punto: tratta SEMPRE come separatore decimale.
    // Il formato italiano con migliaia usa anche la virgola decimale ("1.000,50"),
    // già gestito dal primo ramo. Un singolo punto senza virgola è quasi sempre
    // il separatore decimale — es. prezzi forex "1.09250", "141.250", "2065.40".
    // Unica eccezione: più punti di fila → sicuramente migliaia ("1.000.000").
    else if (str.includes('.')) {
      const parts = str.split('.');
      if (parts.length > 2) {
        // Es: "1.000.000" → migliaia → rimuovi tutti i punti
        str = str.replace(/\./g, '');
      }
      // Singolo punto → decimale → lascia invariato
    }

    // Converti a numero
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  }

  /**
   * Pattern avanzati per il riconoscimento delle colonne
   * Supporta varianti in Italiano, Inglese, Tedesco, Spagnolo
   */
  private static readonly COLUMN_PATTERNS: ColumnPattern[] = [
    {
      field: 'id',
      patterns: [
        /^(id|position|ticket|order|trade|deal|posizione|ordine)(_?id)?$/i,
        /^(deal|position)_?(id|number|no)$/i,
        /^order(_?id)?$/i,
        /^order\s+id$/i // 🆕 cTrader: "Order ID"
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        return str !== '' && !isNaN(Number(str));
      }],
      platformHints: ['MT4', 'MT5', 'cTrader', 'NinjaTrader']
    },
    {
      field: 'symbol',
      patterns: [
        /^(symbol|pair|instrument|asset|market|strumento|simbolo)$/i,
        /^(currency|forex)_?pair$/i,
        /^text$/i, // 🆕 Generic "Text" column (often used for symbol in custom files)
        /^nome$/i // 🆕 Italian "Nome" = Name
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        return str !== '' && str.length >= 2;
      }],
      platformHints: ['ALL']
    },
    {
      field: 'type',
      patterns: [
        /^(type|side|direction|action|operation|tipo|direzione|azione)$/i,
        /^(buy|sell|long|short)$/i,
        /^order_?type$/i,
        /^direzione\s*apertura$/i, // cTrader IT
        /^direzione$/i, // 🆕 File Excel personalizzati IT
        /^opening\s+direction$/i, // 🆕 cTrader: "Opening direction"
        /^closing\s+direction$/i // 🆕 cTrader: "Closing direction"
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        return /^(buy|sell|long|short|call|put|acquisto|vendita)$/i.test(str);
      }],
      platformHints: ['ALL']
    },
    {
      field: 'quantity',
      patterns: [
        /^(quantity|volume|size|lots|amount|units?|quantita|volume|lotti)$/i,
        /^(contract|position)_?size$/i,
        /^lot$/i,
        /^quantit[aà]\s*di\s*chiusura$/i, // 🆕 cTrader IT
        /^volume\s*di\s*chiusura$/i, // 🆕 cTrader IT
        /^closing\s+quantity$/i // 🆕 cTrader: "Closing Quantity"
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        const num = this.parseItalianNumber(str.replace(/\s*lotti?\s*/gi, ''));
        return num !== null && num > 0;
      }],
      platformHints: ['ALL']
    },
    {
      field: 'entryPrice',
      patterns: [
        /^(entry|open|opening|entrata|apertura)(_?price)?$/i,
        /^price(_?open)?$/i,
        /^open$/i,
        /^fill_?price$/i,
        /^prezzo\s*(di\s*)?(entrata|apertura)$/i, // 🆕 IT: "Prezzo Entrata", "Prezzo di entrata", "Prezzo apertura"
        /^entry\s+price$/i, // 🆕 cTrader: "Entry price"
        /^opening\s+price$/i // 🆕 "Opening price"
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        const num = this.parseItalianNumber(str);
        return num !== null && num > 0;
      }],
      platformHints: ['ALL']
    },
    {
      field: 'exitPrice',
      patterns: [
        /^(exit|close|closing|uscita|chiusura)(_?price)?$/i,
        /^price(_?close)?$/i,
        /^close$/i,
        /^prezzo\s*(di\s*)?(uscita|chiusura)$/i, // 🆕 IT: "Prezzo Uscita", "Prezzo di uscita", "Prezzo chiusura"
        /^closing\s+price$/i, // 🆕 cTrader: "Closing price"
        /^exit\s+price$/i, // 🆕 "Exit price"
        /^price_[12]$/i // 🔧 BUG 3 FIX: MT5 deduplicated "Price" columns (Price_1, Price_2) → exitPrice
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        if (str === '') return true; // può essere vuoto
        const num = this.parseItalianNumber(str);
        return num !== null && num > 0;
      }],
      platformHints: ['ALL']
    },
    {
      field: 'stopLoss',
      patterns: [
        /^(stop|sl|stop_?loss)(_?price)?$/i,
        /^s\/?l$/i
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        if (str === '') return true;
        const num = this.parseItalianNumber(str);
        return num !== null && num > 0;
      }],
      platformHints: ['ALL']
    },
    {
      field: 'takeProfit',
      patterns: [
        /^(take|tp|take_?profit)(_?price)?$/i,
        /^t\/?p$/i
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        if (str === '') return true;
        const num = this.parseItalianNumber(str);
        return num !== null && num > 0;
      }],
      platformHints: ['ALL']
    },
    {
      field: 'profit',
      patterns: [
        /^(profit|pnl|p&l|gain|loss|net|result|profitto|perdita)$/i,
        /^(net_?)?(profit|pnl|gain)$/i,
        /^(gross_?)?profit$/i,
        /^profit_?loss$/i,
        /^netto\s*in\s*\$?$/i, // cTrader IT
        /^netto$/i, // cTrader IT
        /^profitto\s*(netto|lordo|totale)?$/i, // 🆕 IT: "Profitto Netto", "Profitto Lordo", "Profitto"
        /^perdita\s*(netta)?$/i, // 🆕 IT: "Perdita Netta", "Perdita"
        /^%%?$/i, // 🆕 Profit percentage column
        /^%\s*(profit|gain|pnl)?$/i, // 🆕 Variations
        /^gross\s*\$$/i, // 🆕 cTrader: "Gross $"
        /^net\s*\$$/i // 🆕 cTrader: "Net $"
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        const num = this.parseItalianNumber(str);
        return num !== null; // Può essere negativo
      }],
      platformHints: ['ALL']
    },
    {
      field: 'commission',
      patterns: [
        /^(commission|fee|fees|cost|spread|commissione|costo)$/i,
        /^(trading_?)?fee$/i
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        if (str === '') return true;
        const num = this.parseItalianNumber(str);
        return num !== null;
      }],
      platformHints: ['ALL']
    },
    {
      field: 'swap',
      patterns: [
        /^(swap|rollover|financing|overnight)$/i,
        /^(swap|rollover)_?(fee|cost)?$/i
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        if (str === '') return true;
        const num = this.parseItalianNumber(str);
        return num !== null;
      }],
      platformHints: ['MT4', 'MT5', 'cTrader']
    },
    {
      field: 'date',
      patterns: [
        /^(date|day|data|fecha)$/i,
        /^(open|entry|created)_?date$/i,
        /^time$/i, // Alcuni CSV hanno "Time" che contiene data+ora
        /^giorno$/i // 🆕 Italian "Giorno" = Day
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        return this.isValidDate(str);
      }],
      platformHints: ['ALL']
    },
    {
      field: 'entryTime',
      patterns: [
        /^(time|open_?time|entry_?time|ora)$/i,
        /^(opened?|created)(_?at)?$/i,
        /^orario\s*di\s*apertura.*$/i, // cTrader IT (include UTC-4)
        /^orario\s*apertura$/i, // 🆕 Excel IT (without "di")
        /^orario\s*(di\s*)?entrata.*$/i, // 🆕 IT: "Orario Entrata", "Orario di entrata (UTC-4)"
        /^data\s*apertura$/i, // cTrader IT
        /^data\s*entrata$/i, // 🆕 IT
        /^opening\s+time$/i, // 🆕 cTrader: "Opening time"
        /^entry\s+time$/i // 🆕 "Entry time"
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        return this.isValidTime(str) || this.isValidDate(str);
      }],
      platformHints: ['ALL']
    },
    {
      field: 'exitTime',
      patterns: [
        /^(close_?time|exit_?time|closed)$/i,
        /^closed?(_?at)?$/i,
        /^orario\s*di\s*chiusura.*$/i, // 🆕 cTrader IT (include UTC-4)
        /^orario\s*(di\s*)?uscita.*$/i, // 🆕 IT: "Orario Uscita", "Orario di uscita (UTC-4)"
        /^data\s*chiusura$/i, // 🆕 cTrader IT
        /^data\s*uscita$/i, // 🆕 IT
        /^closing\s+time$/i, // 🆕 cTrader: "Closing time"
        /^exit\s+time$/i, // 🆕 "Exit time"
        /^time_2$/i // 🆕 MT4 statement: duplicate "Time" column deduplicated to "Time_2"
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        if (str === '') return true;
        return this.isValidTime(str) || this.isValidDate(str);
      }],
      platformHints: ['ALL']
    },
    {
      field: 'durationSec',
      patterns: [
        /^(duration|time_?open|holding_?time)$/i,
        /^duration(_?sec|_?seconds?)?$/i
      ],
      validators: [(v) => {
        const str = String(v ?? '').trim();
        if (str === '') return true;
        return !isNaN(Number(str));
      }],
      platformHints: ['MT4', 'MT5']
    }
  ];

  /**
   * Template per piattaforme specifiche
   * Permette riconoscimento istantaneo e mapping ottimizzato
   */
  private static readonly PLATFORM_TEMPLATES: PlatformTemplate[] = [
    {
      name: 'cTrader',
      signatures: [
        /position\s*id/i,
        /order\s*id/i, // 🆕 cTrader export format
        /ctrader/i,
        /entry\s*time/i,
        /opening\s*time/i, // 🆕 cTrader export format
        /opening\s*direction/i // 🆕 cTrader export format
      ],
      columnMappings: {
        'Position ID': 'id',
        'Order ID': 'id', // 🆕 cTrader export format
        'Symbol': 'symbol',
        'Label': 'type',
        'Opening direction': 'type', // 🆕 cTrader export format
        'Closing direction': 'type', // 🆕 cTrader export format (fallback)
        'Entry Time': 'entryTime',
        'Opening time': 'entryTime', // 🆕 cTrader export format
        'Entry Price': 'entryPrice',
        'Entry price': 'entryPrice', // 🆕 cTrader export format
        'Entry': 'entryPrice', // 🆕 short alias used in some cTrader exports
        'Exit Price': 'exitPrice', // 🆕
        'Exit price': 'exitPrice', // 🆕
        'Exit': 'exitPrice', // 🆕 short alias used in some cTrader exports
        'Closing Time': 'exitTime',
        'Closing time': 'exitTime', // 🆕 cTrader export format
        'Closing Price': 'exitPrice',
        'Closing price': 'exitPrice', // 🆕 cTrader export format
        'Quantity': 'quantity',
        'Volume': 'quantity', // 🆕 volume alias
        'Closing Quantity': 'quantity', // 🆕 cTrader export format
        'Commission': 'commission',
        'Swap': 'swap', // 🆕 cTrader export format
        'Balance': 'profit',
        'Pips': 'profit',
        'Net Profit': 'profit',
        'Gross': 'profit', // 🆕 short alias
        'Gross $': 'profit', // 🆕 cTrader export format
        'Net $': 'profit' // 🆕 cTrader export format (preferred)
      },
      confidence: 0.95
    },
    {
      name: 'cTrader (Italiano)',
      signatures: [
        /simbolo/i,
        /direzione\s*apertura/i,
        /orario\s*di\s*chiusura/i,
        /prezzo\s*di\s*entrata/i,
        /quantit[aà]\s*di\s*chiusura/i,
        /netto\s*in/i
      ],
      columnMappings: {
        'Simbolo': 'symbol',
        'simbolo': 'symbol',
        'symbol': 'symbol', // 🆕 Fallback per DataTable pre-processing
        'Direzione apertura': 'type',
        'Orario di chiusura (UTC-4)': 'exitTime',
        'Orario di chiusura': 'exitTime',
        'Prezzo di entrata': 'entryPrice',
        'Prezzo di chiusura': 'exitPrice',
        'Quantità di Chiusura': 'quantity',
        'Quantita di Chiusura': 'quantity',
        'Volume di chiusura': 'quantity',
        'Netto in $': 'profit',
        'Netto': 'profit',
        'profit in $': 'profit', // 🆕 Fallback per DataTable pre-processing
        'profit': 'profit', // 🆕 Fallback per DataTable pre-processing
        'Saldo $': 'commission',
        'Commissione': 'commission'
      },
      confidence: 0.95
    },
    {
      // MT4 account statement pasted/exported as tab-separated text.
      // Headers: # | Time | Symbol | Type | Volume | Price | S/L | T/P | Time | Price | Commission | Swap | Profit
      // After deduplication: Time_2 = close time, Price_2 = close price.
      name: 'MetaTrader 4 (Statement)',
      signatures: [
        /time_2/i,   // deduplicated "Time" column → only appears in MT4 statement format
        /price_2/i   // deduplicated "Price" column → same
      ],
      columnMappings: {
        '#': 'id',
        'Time': 'entryTime',
        'Symbol': 'symbol',
        'Type': 'type',
        'Volume': 'quantity',
        'Price': 'entryPrice',
        'S/L': 'stopLoss',
        'T/P': 'takeProfit',
        'Time_2': 'exitTime',
        'Price_2': 'exitPrice',
        'Commission': 'commission',
        'Swap': 'swap',
        'Profit': 'profit'
      },
      confidence: 0.97
    },
    {
      // MT4 History tab export: uses 'Item' for symbol, 'Size' for volume,
      // 'S / L' / 'T / P' with spaces, 'Price' = entry, 'Price_2' = exit.
      name: 'MetaTrader 4 (History)',
      signatures: [
        /^ticket$/i,
        /^item$/i,   // 'Item' column is unique to MT4 History format
      ],
      columnMappings: {
        'Ticket':     'id',
        'Open Time':  'entryTime',
        'Type':       'type',
        'Size':       'quantity',
        'Item':       'symbol',
        'Price':      'entryPrice',
        'S / L':      'stopLoss',
        'T / P':      'takeProfit',
        'Close Time': 'exitTime',
        'Price_2':    'exitPrice',
        'Commission': 'commission',
        'Swap':       'swap',
        'Profit':     'profit'
      },
      confidence: 0.97
    },
    {
      name: 'MetaTrader 4',
      signatures: [
        /ticket/i,
        /metatrader/i,
        /^time$/i
      ],
      columnMappings: {
        'Ticket': 'id',
        'Symbol': 'symbol',
        'Type': 'type',
        'Volume': 'quantity',
        'Open Time': 'entryTime',
        'Open Price': 'entryPrice',
        'Close Time': 'exitTime',
        'Close Price': 'exitPrice',
        'S/L': 'stopLoss',
        'T/P': 'takeProfit',
        'Profit': 'profit',
        'Commission': 'commission',
        'Swap': 'swap'
      },
      confidence: 0.95
    },
    {
      name: 'MetaTrader 5',
      signatures: [
        /deal/i,
        /position/i,
        /mt5/i
      ],
      columnMappings: {
        'Deal': 'id',
        'Position ID': 'id',
        'Symbol': 'symbol',
        'Direction': 'type',
        'Volume': 'quantity',
        'Open Time': 'entryTime',
        'Open Price': 'entryPrice',
        'Close Time': 'exitTime',
        'Close Price': 'exitPrice',
        'SL': 'stopLoss',
        'TP': 'takeProfit',
        'Profit': 'profit',
        'Commission': 'commission',
        'Swap': 'swap'
      },
      confidence: 0.95
    },
    {
      name: 'Interactive Brokers',
      signatures: [
        /account\s*id/i,
        /ibkr/i,
        /ib_/i
      ],
      columnMappings: {
        'TradeID': 'id',
        'Symbol': 'symbol',
        'Buy/Sell': 'type',
        'Quantity': 'quantity',
        'T. Price': 'entryPrice',
        'C. Price': 'exitPrice',
        'Comm/Fee': 'commission',
        'Realized P/L': 'profit',
        'DateTime': 'entryTime'
      },
      confidence: 0.90
    },
    {
      name: 'NinjaTrader',
      signatures: [
        /instrument/i,
        /ninja/i,
        /entry\s*name/i,
        /market\s*pos/i
      ],
      columnMappings: {
        // Orders format
        'Order ID': 'id',
        'Action': 'type',
        'Avg Fill Price': 'entryPrice',
        'Avg Exit Price': 'exitPrice',
        'Time': 'entryTime',
        // Trades/Strategy format
        'Instrument': 'symbol',
        'Market pos.': 'type',
        'Entry price': 'entryPrice',
        'Exit price': 'exitPrice',
        'Entry time': 'entryTime',
        'Exit time': 'exitTime',
        'Strategy': 'strategy',
        // Common
        'Quantity': 'quantity',
        'Profit': 'profit',
        'Commission': 'commission',
      },
      confidence: 0.90
    },
    {
      name: 'TradingView',
      signatures: [
        /tradingview/i,
        /paper\s*trading/i,
        /filled\s*qty/i
      ],
      columnMappings: {
        'Order ID': 'id',
        'Symbol': 'symbol',
        'Type': 'type',
        'Side': 'type',
        'Filled Qty': 'quantity',
        'Qty': 'quantity',
        'Avg Price': 'entryPrice',
        'Price': 'entryPrice',
        'Exit Price': 'exitPrice',
        'P&L': 'profit',
        'Profit/Loss': 'profit',
        'Commission': 'commission',
        'Date/Time': 'entryTime',
        'Time': 'entryTime'
      },
      confidence: 0.90
    },
    {
      name: 'ThinkorSwim',
      signatures: [
        /thinkorswim/i,
        /td\s*ameritrade/i,
        /exec\s*time/i
      ],
      columnMappings: {
        'Trade ID': 'id',
        'Order ID': 'id',
        'Symbol': 'symbol',
        'Side': 'type',
        'Qty': 'quantity',
        'Exec Time': 'entryTime',
        'Price': 'entryPrice',
        'Net Price': 'entryPrice',
        'Close Price': 'exitPrice',
        'P/L': 'profit',
        'Comm': 'commission',
        'Fees': 'commission'
      },
      confidence: 0.90
    },
    {
      name: 'TradeZero',
      signatures: [
        /tradezero/i,
        /account\s*number/i,
        /route/i
      ],
      columnMappings: {
        'Order No': 'id',
        'Symbol': 'symbol',
        'Side': 'type',
        'B/S': 'type',
        'Shares': 'quantity',
        'Qty': 'quantity',
        'Price': 'entryPrice',
        'Exec Price': 'entryPrice',
        'Time': 'entryTime',
        'P&L': 'profit',
        'Realized': 'profit',
        'Commission': 'commission'
      },
      confidence: 0.88
    },
    {
      name: 'Webull',
      signatures: [
        /webull/i,
        /filled\s*time/i,
        /average\s*cost/i
      ],
      columnMappings: {
        'Order ID': 'id',
        'Symbol': 'symbol',
        'Action': 'type',
        'Side': 'type',
        'Filled Qty': 'quantity',
        'Quantity': 'quantity',
        'Average Cost': 'entryPrice',
        'Avg Cost': 'entryPrice',
        'Filled Time': 'entryTime',
        'Total': 'profit',
        'P/L': 'profit',
        'Fee': 'commission'
      },
      confidence: 0.88
    },
    {
      name: 'eToro',
      signatures: [
        /etoro/i,
        /position\s*id/i,
        /copy\s*trader/i
      ],
      columnMappings: {
        'Position ID': 'id',
        'Instrument': 'symbol',
        'Action': 'type',
        'Type': 'type',
        'Amount': 'quantity',
        'Units': 'quantity',
        'Open Rate': 'entryPrice',
        'Close Rate': 'exitPrice',
        'Opened': 'entryTime',
        'Closed': 'exitTime',
        'Profit': 'profit',
        'Spread': 'commission'
      },
      confidence: 0.88
    },
    {
      name: 'Plus500',
      signatures: [
        /plus500/i,
        /position\s*number/i,
        /opening\s*rate/i
      ],
      columnMappings: {
        'Position Number': 'id',
        'Instrument': 'symbol',
        'Action': 'type',
        'Amount': 'quantity',
        'Opening Rate': 'entryPrice',
        'Closing Rate': 'exitPrice',
        'Opening Date': 'entryTime',
        'Closing Date': 'exitTime',
        'Profit/Loss': 'profit',
        'Overnight Funding': 'swap'
      },
      confidence: 0.88
    },
    {
      name: 'Binance (Spot)',
      signatures: [
        /binance/i,
        /order\s*id/i,
        /executed/i
      ],
      columnMappings: {
        'Order ID': 'id',
        'Pair': 'symbol',
        'Symbol': 'symbol',
        'Side': 'type',
        'Type': 'type',
        'Executed': 'quantity',
        'Amount': 'quantity',
        'Price': 'entryPrice',
        'Average': 'entryPrice',
        'Time': 'entryTime',
        'Total': 'profit',
        'Fee': 'commission'
      },
      confidence: 0.85
    },
    {
      name: 'Bybit',
      signatures: [
        /bybit/i,
        /order\s*id/i,
        /avg\s*entry\s*price/i
      ],
      columnMappings: {
        'Order ID': 'id',
        'Symbol': 'symbol',
        'Side': 'type',
        'Qty': 'quantity',
        'Avg Entry Price': 'entryPrice',
        'Avg Exit Price': 'exitPrice',
        'Created Time': 'entryTime',
        'Closed P&L': 'profit',
        'Closed PnL': 'profit',
        'Funding Fee': 'swap',
        'Trading Fee': 'commission'
      },
      confidence: 0.85
    }
  ];

  /**
   * Analizza le colonne del CSV e ritorna il mapping ottimale
   * 🆕 V5: Integrato con UserMappingService per apprendimento dinamico
   */
  static analyze(headers: string[], sampleData: string[][]): ColumnAnalysisResult {
    console.log('🔍 EnhancedColumnIdentifier: Avvio analisi...');

    // 🆕 0. Carica mappature utente salvate
    const userMappings = this.loadUserMappings();
    console.log(`📖 ${userMappings.size} mappature utente caricate`);

    // 🆕 0.5. PRE-ANALISI: Rileva e gestisce timestamp Unix (entryTime/closeTime)
    const timestampMapping = this.detectAndMapTimestamps(headers, sampleData);
    console.log(`⏰ Timestamp Unix rilevati:`, timestampMapping);

    // 1. Detect platform prima
    const detectedPlatform = this.detectPlatform(headers);
    console.log(`🎯 Platform detected: ${detectedPlatform || 'Generic'}`);

    // 2. Se piattaforma riconosciuta, usa template predefinito
    if (detectedPlatform) {
      const template = this.PLATFORM_TEMPLATES.find(t => t.name === detectedPlatform);
      if (template) {
        const result = this.applyPlatformTemplate(template, headers, sampleData);

        // 🆕 FIX: se il template lascia colonne senza mapping, usa pattern matching
        // su quelle colonne (es. "Entry"/"Exit" non presenti nel template standard)
        if (result.unmappedColumns.length > 0) {
          const fallback = this.analyzeWithPatterns(result.unmappedColumns, sampleData, detectedPlatform, headers);
          // Merge fallback mappings (template has priority for already-mapped columns)
          result.mapping = { ...fallback.mapping, ...result.mapping };
          result.unmappedColumns = fallback.unmappedColumns;
          console.log(`🔧 Pattern fallback per colonne non mappate dal template:`, fallback.mapping);
        }

        // 🆕 Applica anche mappature utente per colonne non mappate dal template
        result.mapping = this.applyUserMappings(headers, result.mapping, userMappings) as any;
        return result;
      }
    }

    // 3. Analisi pattern-based
    const result = this.analyzeWithPatterns(headers, sampleData, null);

    // 🆕 3.5. Applica mappature timestamp (ha priorità su tutto)
    if (timestampMapping && Object.keys(timestampMapping).length > 0) {
      result.mapping = { ...result.mapping, ...timestampMapping };
      console.log(`✅ Mappature timestamp applicate:`, result.mapping);
    }
    
    // 🆕 4. Applica mappature utente per colonne non ancora mappate
    result.mapping = this.applyUserMappings(headers, result.mapping, userMappings) as any;

    // 🆕 5. Ricalcola confidence considerando le mappature utente
    const totalMapped = Object.keys(result.mapping).length;
    if (totalMapped > 0) {
      result.confidence = Math.min(1, result.confidence + (totalMapped / headers.length) * 0.15);
    }
    
    return result;
  }

  /**
   * Rileva la piattaforma dai nomi delle colonne
   */
  private static detectPlatform(headers: string[]): string | null {
    const headerString = headers.join('|').toLowerCase();

    for (const template of this.PLATFORM_TEMPLATES) {
      let matches = 0;
      for (const signature of template.signatures) {
        if (signature.test(headerString)) {
          matches++;
        }
      }

      // Se almeno 2 signature matchano, consideriamo la piattaforma identificata
      if (matches >= 2 || (matches >= 1 && template.signatures.length === 1)) {
        return template.name;
      }
    }

    return null;
  }

  /**
   * Applica un template predefinito per una piattaforma
   */
  private static applyPlatformTemplate(
    template: PlatformTemplate,
    headers: string[],
    sampleData: string[][]
  ): ColumnAnalysisResult {
    const mapping: { [csvHeader: string]: keyof StandardTrade } = {};
    const unmappedColumns: string[] = [];

    for (const header of headers) {
      if (template.columnMappings[header]) {
        mapping[header] = template.columnMappings[header];
      } else {
        // Prova match case-insensitive
        const headerLower = header.toLowerCase();
        const matchedKey = Object.keys(template.columnMappings).find(
          k => k.toLowerCase() === headerLower
        );
        
        if (matchedKey) {
          mapping[header] = template.columnMappings[matchedKey];
        } else {
          unmappedColumns.push(header);
        }
      }
    }

    // Valida con sample data
    const validationScore = this.validateMapping(mapping, headers, sampleData);
    const finalConfidence = (template.confidence + validationScore) / 2;

    console.log(`✅ Template applicato: ${template.name} (Confidence: ${(finalConfidence * 100).toFixed(1)}%)`);

    return {
      mapping,
      confidence: finalConfidence,
      detectedPlatform: template.name,
      unmappedColumns
    };
  }

  /**
   * Analisi basata su pattern (fallback per piattaforme sconosciute)
   */
  private static analyzeWithPatterns(
    headers: string[],
    sampleData: string[][],
    platformHint: string | null,
    fullHeaders?: string[] // When headers is a subset, use fullHeaders for column index lookup
  ): ColumnAnalysisResult {
    const mapping: { [csvHeader: string]: keyof StandardTrade } = {};
    const unmappedColumns: string[] = [];
    let totalConfidence = 0;
    let mappedCount = 0;

    // Use fullHeaders for index lookup when headers is a subset (e.g. unmapped columns only)
    const lookupHeaders = fullHeaders || headers;

    for (const header of headers) {
      // If the header is already a StandardTrade field name (remapped by onImport), trust it directly
      const directField = this.COLUMN_PATTERNS.find(p => p.field === header);
      if (directField) {
        mapping[header] = header as keyof StandardTrade;
        totalConfidence += 0.95;
        mappedCount++;
        continue;
      }

      let bestMatch: { field: keyof StandardTrade; confidence: number } | null = null;

      for (const pattern of this.COLUMN_PATTERNS) {
        // Check platform hint se disponibile
        if (platformHint && pattern.platformHints && !pattern.platformHints.includes('ALL')) {
          if (!pattern.platformHints.includes(platformHint)) {
            continue;
          }
        }

        for (const regex of pattern.patterns) {
          // Match the header as written and with its spaces closed up.
          //
          // The patterns are anchored and spell separators as an optional
          // underscore — `^(time|open_?time|entry_?time)$`. MetaTrader writes
          // "Open Time" and "Open Price", with a space, so none of them matched
          // and the entry/exit prices and times of an ordinary MT4 statement
          // came through unmapped. Trying the collapsed form costs one extra
          // regex test and covers every export that separates words with
          // spaces, dots or hyphens instead of underscores.
          const collapsed = header.replace(/[\s.\-/]+/g, '_');
          if (regex.test(header) || (collapsed !== header && regex.test(collapsed))) {
            let confidence = 0.7; // Base confidence

            // Valida con sample data (usa indice nella lista completa degli headers)
            const colIndex = lookupHeaders.indexOf(header);
            const validCount = sampleData.reduce((count, row) => {
              const value = (colIndex !== -1 ? row[colIndex] : undefined) || '';
              return pattern.validators.every(validator => validator(value)) ? count + 1 : count;
            }, 0);

            const dataConfidence = sampleData.length > 0 ? validCount / sampleData.length : 0;
            confidence = (confidence + dataConfidence) / 2;

            if (!bestMatch || confidence > bestMatch.confidence) {
              bestMatch = { field: pattern.field, confidence };
            }
          }
        }
      }

      if (bestMatch && bestMatch.confidence > 0.5) {
        // Gestione duplicati: prima occorrenza = entry, seconda = exit
        const existingEntry = Object.entries(mapping).find(([_, field]) => field === bestMatch!.field);
        if (existingEntry) {
          const existingHeader = existingEntry[0];
          const existingIndex = headers.indexOf(existingHeader);
          const newIndex = headers.indexOf(header);

          if (existingIndex < newIndex) {
            // La colonna già mappata è PRIMA → è entry. Questa (nuova) è exit.
            const exitField = bestMatch.field === 'entryPrice' ? 'exitPrice'
                            : bestMatch.field === 'entryTime'  ? 'exitTime'
                            : null;
            if (exitField) {
              mapping[header] = exitField as keyof StandardTrade;
            } else {
              unmappedColumns.push(header);
            }
          } else {
            // La colonna già mappata è DOPO → sostituisci con questa (più a sinistra)
            delete mapping[existingHeader];
            unmappedColumns.push(existingHeader);
            mapping[header] = bestMatch.field;
          }
        } else {
          mapping[header] = bestMatch.field;
        }
        
        totalConfidence += bestMatch.confidence;
        mappedCount++;
      } else {
        unmappedColumns.push(header);
      }
    }

    const overallConfidence = mappedCount > 0 ? totalConfidence / mappedCount : 0;

    console.log(`📊 Pattern analysis: ${mappedCount}/${headers.length} colonne mappate (${(overallConfidence * 100).toFixed(1)}%)`);

    return {
      mapping,
      confidence: overallConfidence,
      detectedPlatform: platformHint,
      unmappedColumns
    };
  }

  /**
   * Valida un mapping con sample data
   */
  private static validateMapping(
    mapping: { [csvHeader: string]: keyof StandardTrade },
    headers: string[],
    sampleData: string[][]
  ): number {
    let validCount = 0;
    let totalChecks = 0;

    for (const [header, field] of Object.entries(mapping)) {
      const columnIndex = headers.indexOf(header);
      if (columnIndex === -1) continue;

      const pattern = this.COLUMN_PATTERNS.find(p => p.field === field);
      if (!pattern) continue;

      for (const row of sampleData) {
        const value = row[columnIndex] || '';
        if (pattern.validators.every(v => v(value))) {
          validCount++;
        }
        totalChecks++;
      }
    }

    return totalChecks > 0 ? validCount / totalChecks : 0;
  }

  /**
   * Trasforma un valore raw nel tipo corretto per il campo
   */
  static transformValue(field: keyof StandardTrade, rawValue: string | number | any): any {
    // 🔧 FIX: Converti sempre a stringa prima di processare
    const value = String(rawValue ?? '').trim();

    switch (field) {
      case 'id':
        return value;

      case 'symbol':
        return value.toUpperCase();

      case 'type':
        const typeUpper = value.toUpperCase();
        return ['LONG', 'BUY', 'CALL', 'ACQUISTO'].includes(typeUpper) ? 'BUY' : 'SELL';

      case 'quantity':
        // 🆕 Gestisce formato italiano: "0.10 Lotti" → 0.10, "0,02" → 0.02
        const cleanQuantity = value.replace(/\s*lotti?\s*/gi, '').trim();
        return this.parseItalianNumber(cleanQuantity) || 0;

      case 'entryPrice':
      case 'exitPrice': {
        // Prezzi non possono mai essere 0 su un trade reale — undefined segnala problema di mapping
        const priceNum = this.parseItalianNumber(value);
        return (priceNum !== null && priceNum !== 0) ? priceNum : undefined;
      }
      case 'stopLoss':
      case 'takeProfit':
      case 'profit':
      case 'commission':
      case 'swap':
      case 'durationSec':
        // 🆕 Usa parseItalianNumber per gestire tutti i formati
        // "76,304" → 76.304, "-366,52" → -366.52, "€1.000,50" → 1000.50
        return this.parseItalianNumber(value) || 0;

      case 'date':
        return this.formatDate(value);

      case 'entryTime':
      case 'exitTime':
        return this.formatTime(value);

      default:
        return value;
    }
  }

  /**
   * Valida se una stringa è una data valida
   */
  private static isValidDate(value: string): boolean {
    if (!value || value.trim() === '') return false;
    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  /**
   * Formatta una data in formato ISO (YYYY-MM-DD)
   * 🔧 PUBLIC per essere accessibile da DataTransformationBridge
   */
  static formatDate(value: string): string {
    try {
      const raw = (value || '').trim();
      if (!raw) return value;

      // Rimuove eventuale giorno della settimana (lun, mar, mer, gio, ven, sab, dom)
      const noWeekday = raw.replace(/^(lun|mar|mer|gio|ven|sab|dom)\s+/i, '');

      // NinjaTrader (American): "1/2/2024 8:15:32 AM" — M/D/YYYY, detect via AM/PM
      if (noWeekday.includes('/') && /\b(AM|PM)\b/i.test(noWeekday)) {
        const [datePart] = noWeekday.split(' ');
        const m = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (m) {
          const [, month, day, year] = m;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }

      // Formato italiano con timestamp: "18/11/2024 12:13:17.276"
      if (noWeekday.includes('/') && noWeekday.includes(' ')) {
        const [datePart] = noWeekday.split(' ');
        const m = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (m) {
          const [, d, mth, y] = m;
          return `${y}-${mth.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
      }

      // Formato MT4/MT5 con punti e timestamp: "2024.01.02 08:15:32"
      if (noWeekday.includes('.') && noWeekday.includes(' ')) {
        const [datePart] = noWeekday.split(' ');
        const m4 = datePart.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/);
        if (m4) {
          const [, y, mth, d] = m4;
          return `${y}-${mth.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
      }

      // Formato MT4/MT5 con punti: "2024.01.02" o solo la parte dataPart
      // deve essere riconosciuto prima del fallback generico
      const mt4Dot = noWeekday.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/);
      if (mt4Dot) {
        const [, y, m, d] = mt4Dot;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }

      // Puro formato italiano DD/MM/YYYY
      const it = noWeekday.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (it) {
        const [, d, mth, y] = it;
        return `${y}-${mth.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }

      // Gestisce formato ISO o parseabile da Date
      const date = new Date(noWeekday);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      // Tentativo extra: DD-MM-YYYY o DD.MM.YYYY
      const parts = noWeekday.split(/[\/\-\.]/);
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const normalizedDate = new Date(`${year}-${month}-${day}`);
        if (!isNaN(normalizedDate.getTime())) {
          return normalizedDate.toISOString().split('T')[0];
        }
      }

      return value; // fallback
    } catch {
      return value;
    }
  }

  /**
   * Valida se una stringa è un orario valido
   */
  private static isValidTime(value: string): boolean {
    if (!value || value.trim() === '') return false;
    
    // Accept formats: HH:MM, HH:MM:SS, or full ISO datetime
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
    if (timeRegex.test(value)) return true;
    
    // 🆕 Gestisce formato italiano: "18/11/2024 12:13:17.276"
    if (value.includes('/') && value.includes(':')) return true;
    
    // Check if it's a full datetime string
    return this.isValidDate(value);
  }

  /**
   * Formatta un orario in formato HH:MM:SS
   * 🔧 PUBLIC per essere accessibile da DataTransformationBridge
   */
  static formatTime(value: string): string {
    try {
      // NinjaTrader: "1/2/2024 8:15:32 AM" — parse AM/PM correctly
      if (value.includes('/') && /\b(AM|PM)\b/i.test(value)) {
        const match = value.match(/(\d{1,2}:\d{2}(?::\d{2})?)\s*(AM|PM)/i);
        if (match) {
          const d = new Date(`1970-01-01 ${match[1]} ${match[2].toUpperCase()}`);
          if (!isNaN(d.getTime())) return d.toTimeString().split(' ')[0];
        }
      }

      // cTrader/Italian: "18/11/2024 12:13:17.276"
      if (value.includes('/') && value.includes(' ')) {
        const [, timePart] = value.split(' ');
        return timePart.split('.')[0];
      }

      // MT4/MT5: "2024.01.02 08:15:32" or "2024.01.02 08:15:32.000"
      if (/^\d{4}\./.test(value) && value.includes(' ')) {
        const spaceIdx = value.indexOf(' ');
        return value.substring(spaceIdx + 1).split('.')[0];
      }

      // NinjaTrader AM/PM: "8:15:32 AM" or datetime with AM/PM
      if (/\b(AM|PM)\b/i.test(value)) {
        const match = value.match(/(\d{1,2}:\d{2}(?::\d{2})?)\s*(AM|PM)/i);
        if (match) {
          const d = new Date(`1970-01-01 ${match[1]} ${match[2].toUpperCase()}`);
          if (!isNaN(d.getTime())) return d.toTimeString().split(' ')[0];
        }
      }

      if (value.includes('T') || value.includes(' ')) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toTimeString().split(' ')[0];
        }
      }

      // Already time-only or unparseable
      return value;
    } catch {
      return value;
    }
  }

  /**
   * 🆕 Rileva e mappa automaticamente timestamp Unix in entryTime/closeTime
   * Estrae automaticamente date e orari dai timestamp
   */
  private static detectAndMapTimestamps(
    headers: string[],
    sampleData: any[][]
  ): { [csvHeader: string]: keyof StandardTrade } {
    const mapping: { [csvHeader: string]: keyof StandardTrade } = {};
    
    if (sampleData.length === 0) return mapping;

    headers.forEach((header, colIndex) => {
      const normalized = header.toLowerCase().trim();
      
      // Rileva colonne con timestamp Unix (13 cifre)
      const isEntryTime = normalized.includes('entry') && normalized.includes('time') ||
                          normalized.includes('opentime') || normalized === 'entrytime';
      const isCloseTime = normalized.includes('close') && normalized.includes('time') ||
                          normalized.includes('closetime');
      
      if (!isEntryTime && !isCloseTime) return;

      // Verifica se contiene timestamp Unix (controlla primi 3 valori)
      const hasUnixTimestamp = sampleData.slice(0, 3).every(row => {
        const value = row[colIndex];
        if (!value) return false;
        
        const numValue = typeof value === 'number' ? value : parseInt(String(value));
        // Timestamp Unix in millisecondi: 13 cifre (tra 1000000000000 e 9999999999999)
        return !isNaN(numValue) && numValue >= 1000000000000 && numValue <= 9999999999999;
      });

      if (hasUnixTimestamp) {
        console.log(`⏰ Rilevato timestamp Unix in colonna "${header}"`);
        
        if (isEntryTime) {
          // entryTime con timestamp → mappa come 'date' (verrà estratta la data)
          // La data ha priorità assoluta perché è obbligatoria
          mapping[header] = 'date';
          console.log(`✅ "${header}" → date (estratta da timestamp Unix)`);
        } else if (isCloseTime) {
          // closeTime con timestamp → mappa come 'exitTime' (verrà estratto l'orario)
          mapping[header] = 'exitTime';
          console.log(`✅ "${header}" → exitTime (estratto da timestamp Unix)`);
        }
      }
    });

    return mapping;
  }

  /**
   * 🆕 Carica mappature utente dal localStorage
   */
  private static loadUserMappings(): Map<string, string> {
    try {
      // Importazione dinamica per evitare dipendenze circolari
      const stored = localStorage.getItem('tradingspace_user_mappings_v1');
      if (!stored) return new Map();

      const parsed = JSON.parse(stored);
      const mappings = new Map<string, string>();
      
      Object.entries(parsed).forEach(([key, value]: [string, any]) => {
        if (value.confidence >= 75) { // Solo mappature con alta confidence
          mappings.set(key, value.mappedTo);
        }
      });

      return mappings;
    } catch (error) {
      console.error('❌ Errore caricamento mappature utente:', error);
      return new Map();
    }
  }

  /**
   * 🆕 Applica mappature utente durante l'analisi
   */
  private static applyUserMappings(
    headers: string[], 
    mapping: Record<string, string>,
    userMappings: Map<string, string>
  ): Record<string, string> {
    const enhancedMapping = { ...mapping };
    let appliedCount = 0;

    headers.forEach(header => {
      const normalized = header.toLowerCase().trim();
      const userMapping = userMappings.get(normalized);
      
      if (userMapping && !enhancedMapping[header]) {
        enhancedMapping[header] = userMapping;
        appliedCount++;
        console.log(`🧠 Mappatura utente applicata: "${header}" → ${userMapping}`);
      }
    });

    if (appliedCount > 0) {
      console.log(`✅ ${appliedCount} mappature utente applicate`);
    }

    return enhancedMapping;
  }

  /**
   * 🆕 Identifica colonne non mappate per UI conferma
   */
  static getUnmappedColumns(
    headers: string[],
    mapping: Record<string, string>,
    sampleData: string[][] | undefined
  ): Array<{columnName: string, sampleValues: string[], index: number}> {
    const unmapped: Array<{columnName: string, sampleValues: string[], index: number}> = [];

    // 🆕 FIX: Check if sampleData exists
    if (!sampleData || sampleData.length === 0) {
      console.log('⚠️ getUnmappedColumns: sampleData non disponibile');
      // Ritorna colonne non mappate senza valori di esempio
      headers.forEach((header, index) => {
        if (!mapping[header] && header.trim() !== '') {
          unmapped.push({
            columnName: header,
            sampleValues: [],
            index
          });
        }
      });
      return unmapped;
    }

    headers.forEach((header, index) => {
      if (!mapping[header] && header.trim() !== '') {
        // Estrai valori di esempio per questa colonna
        const sampleValues = sampleData
          .slice(0, 5)
          .map(row => row[index])
          .filter(v => v && v.trim() !== '');

        if (sampleValues.length > 0) {
          unmapped.push({
            columnName: header,
            sampleValues,
            index
          });
        }
      }
    });

    return unmapped;
  }
}
